import { RoundedBox } from '@react-three/drei'
import * as THREE from 'three'
import { useVerandaSelector } from '../../../../..'
import { useMeasures } from '../../../../../hooks/state/useMeasures'
import { useRoof } from '../../../../../hooks/state/useRoof'
import { CachedMeshPhysicalMaterial } from '../../../../material/cachedMeshPhysicalMaterial'
import { Holder } from './Holder'
import { useMarquee } from '../../../../../hooks/state/useMarquee'
import { Winkel } from './Winkel'
import { Helper } from '../../../../../classes/helper'

interface Props {
  marqueeWidth: any
  rotation: [number, number, number]
}

export const MarqueeRail = ({ marqueeWidth, rotation }: Props): JSX.Element => {
  const { roofLogWidth, isQubus, eaves, eavesWidth, verandaTyp } = useMeasures()
  const { color } = useRoof()
  const { maqrueeAttributes, marqueeTyp, hasSideWall } = useMarquee()
  const shadingMode = useVerandaSelector((state) => state.scene.shadingMode)

  const topMarquee = rotation[2] === 0
  const roofLength = roofLogWidth - (isQubus ? 0.05 : 0)
  const casing = {
    radius: 0.05,
    width: marqueeWidth,
  }

  const casingRail = {
    width: casing.radius * 2,
    height: casing.radius,
    length: marqueeWidth - 0.001,
  }

  const rail = {
    width: 0.025,
    height: casingRail.height - 0.002,
    length:
      roofLength -
      (marqueeTyp.hasUnderRoof ? eavesWidth + verandaTyp.log.depth : 0),
  }

  const railBottom = {
    width: rail.width,
    height: 0.02,
    length: rail.length,
  }
  const bar = {
    height: maqrueeAttributes.farbric.depth * 1.9,
  }

  const endCapGeometry = new THREE.CylinderGeometry(
    rail.height / 2,
    rail.height / 2,
    rail.width - 0.005,
    32
  )

  return (
    <group
      position={[
        topMarquee && marqueeTyp.hasUnderRoof
          ? railBottom.height + maqrueeAttributes.height
          : marqueeTyp.hasUnderRoof
          ? -railBottom.height + maqrueeAttributes.height + 0.035
          : railBottom.height + maqrueeAttributes.height,
        0,
        0,
      ]}
      castShadow
      receiveShadow
    >
      {[...Array(2)].map((_, index) => {
        return (
          <group
            position={[
              0,
              index
                ? -((casingRail.length - rail.width) / 2 - 0.001)
                : (casingRail.length - rail.width) / 2 - 0.001,
              -rail.length / 2,
            ]}
            key={'MarqueeRail' + index}
          >
            <group rotation={rotation}>
              <RoundedBox
                args={[rail.height, rail.width, rail.length]}
                position={[casing.radius - casingRail.height / 2, 0, 0]}
                radius={0.001}
                castShadow
                receiveShadow
              >
                <CachedMeshPhysicalMaterial color={color} />
              </RoundedBox>
              <RoundedBox
                args={[
                  0.005,
                  bar.height * 1.02,
                  shadingMode ? 0.01 : rail.length,
                ]}
                position={[
                  casingRail.height / 2 + bar.height / 2,
                  rotation[2] === 0
                    ? index
                      ? rail.width / 2
                      : -rail.width / 2
                    : !index
                    ? rail.width / 2
                    : -rail.width / 2,
                  0,
                ]}
                radius={0.00051}
                castShadow
                receiveShadow
              >
                <CachedMeshPhysicalMaterial color="silver" />
              </RoundedBox>

              <mesh
                position={[
                  casing.radius - casingRail.height / 2,
                  0,
                  -rail.length / 2,
                ]}
                geometry={endCapGeometry}
                castShadow
                receiveShadow
              >
                <CachedMeshPhysicalMaterial color={color} />
              </mesh>
            </group>
            {marqueeTyp.hasUnderRoof && hasSideWall && (
              <group
                position={[0, 0, 0]}
                rotation={[index ? Helper.convertAngleToRadiant(180) : 0, 0, 0]}
              >
                <Winkel
                  position={[
                    -rail.height - 0.01,
                    0.01,
                    rail.length / 2 - casingRail.width,
                  ]}
                  spaceY={maqrueeAttributes.height}
                  color={color}
                />
                <Winkel
                  position={[-rail.height - 0.01, 0.01, 0]}
                  spaceY={maqrueeAttributes.height}
                  color={color}
                />
                <Winkel
                  position={[
                    -rail.height - 0.01,
                    0.01,
                    -rail.length / 2 + casingRail.width,
                  ]}
                  spaceY={maqrueeAttributes.height}
                  color={color}
                />
              </group>
            )}
            {(topMarquee || (marqueeTyp.hasUnderRoof && hasSideWall)) && (
              <group>
                <RoundedBox
                  args={[
                    railBottom.height,
                    railBottom.width,
                    railBottom.length,
                  ]}
                  position={[
                    -maqrueeAttributes.height - railBottom.height / 2,
                    0,
                    0,
                  ]}
                  radius={0.0001}
                  castShadow
                  receiveShadow
                >
                  <CachedMeshPhysicalMaterial color={color} />
                </RoundedBox>

                <Holder
                  position={[0, 0, rail.length / 2 - casingRail.width]}
                  spaceY={maqrueeAttributes.height}
                  color={color}
                />
                <Holder
                  position={[0, 0, -rail.length / 2 + casingRail.width]}
                  spaceY={maqrueeAttributes.height}
                  color={color}
                />
              </group>
            )}
          </group>
        )
      })}
    </group>
  )
}
